import { apiClient } from '../api/client';
import { InboxTask } from '../types/workflow';

export interface InboxTasksResponse {
  tasks: InboxTask[];
  total: number;
  pending_count: number;
  page: number;
  limit: number;
}

export interface CompleteTaskInput {
  action: 'approve' | 'reject' | 'complete' | 'upload' | 'sign' | 'send';
  comments?: string;
}

export interface ReminderStatus {
  /** When the task was last reminded (auto or manual), or null if never. */
  lastRemindedAt: string | null;
  /** Last manual reminder, which drives the 12h cooldown; null if none. */
  lastManualRemindedAt: string | null;
  /** When the next automatic reminder is due, or null if none is scheduled. */
  nextReminderAt: string | null;
  /** How many reminders have been sent for this task. */
  sentCount: number;
  /** True when the tenant has paused all workflow notifications. */
  paused: boolean;
}

class InboxTasksService {
  // Authoritative count of the current user's pending tasks (assigned + eligible
  // pools), computed server-side. Single source for the sidebar badge, the Home
  // card and the tasks-view counter.
  async getMyPendingCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/inbox-tasks/my-count');
    return response.count ?? 0;
  }

  async getTasks(params?: {
    status?: string;
    type?: string;
    priority?: string;
    scope?: 'my' | 'all';
    sort?: 'due';
    page?: number;
    limit?: number;
  }): Promise<InboxTasksResponse> {
    // Build query string from params
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/inbox-tasks?${queryString}` : '/inbox-tasks';
    const response = await apiClient.get<any>(endpoint);

    // Transform backend data to frontend format
    const transformedTasks = response.tasks.map((task: any) => ({
      id: task.id,
      type: task.type,
      workflowStep: task.workflow_step,
      title: task.title,
      description: task.description,
      entityCode: task.entity_code,
      entityName: task.entity_name,
      bankName: task.bank_name,
      bankLogoUrl: task.bank_logo_url || undefined,
      accountDescription: task.account_name || task.bank_name, // Use account_name if available, fallback to bank_name
      accountNumber: task.account_number,
      accountCount: task.mandate?.document_data?.bank_accounts?.length || 1,
      submittedBy: task.submitted_by || {
        name: 'Système',
        email: ''
      },
      submittedAt: task.created_at,
      dueDate: task.due_date,
      priority: task.priority || 'medium',
      status: task.status,
      mandateId: task.bank_mandate_id || task.mandate?.id || '',
      assignedToUser: task.assigned_to_user || null,
      mandate: task.mandate || undefined,
      // Use timeline directly from backend
      timeline: task.timeline || undefined,
      // Role required for current workflow step (from workflow definition)
      requiredRole: task.required_role || undefined,
      signatureMethod: task.signature_method || undefined,
      stepType: task.step_type || undefined,
      canAct: task.can_act ?? undefined
    }));

    return {
      ...response,
      tasks: transformedTasks
    };
  }

  async getTask(id: string): Promise<InboxTask> {
    const response = await apiClient.get<any>(`/inbox-tasks/${id}`);
    const task = response;

    // Transform to frontend format
    return {
      id: task.id,
      type: task.type,
      workflowStep: task.workflow_step,
      title: task.title,
      description: task.description,
      entityCode: task.entity_code,
      entityName: task.entity_name,
      bankName: task.bank_name,
      bankLogoUrl: task.bank_logo_url || undefined,
      accountDescription: task.account_name || task.bank_name, // Use account_name if available, fallback to bank_name
      accountNumber: task.account_number,
      accountCount: task.mandate?.document_data?.bank_accounts?.length || 1,
      submittedBy: task.submitted_by || {
        name: 'Système',
        email: ''
      },
      submittedAt: task.created_at,
      dueDate: task.due_date,
      priority: task.priority || 'medium',
      status: task.status,
      mandateId: task.bank_mandate_id || task.mandate?.id || '',
      assignedToUser: task.assigned_to_user || null,
      mandate: task.mandate || undefined,
      // Use timeline directly from backend
      timeline: task.timeline || undefined,
      // Role required for current workflow step (from workflow definition)
      requiredRole: task.required_role || undefined,
      signatureMethod: task.signature_method || undefined,
      stepType: task.step_type || undefined,
      canAct: task.can_act ?? undefined
    };
  }

  async completeTask(id: string, input: CompleteTaskInput): Promise<void> {
    await apiClient.patch(`/inbox-tasks/${id}/complete`, input);
  }

  async reassignTask(id: string, newUserId: string, reason?: string): Promise<void> {
    await apiClient.patch(`/inbox-tasks/${id}/reassign`, {
      new_user_id: newUserId,
      reason
    });
  }

  /**
   * Manually remind the current assignee of a pending task. The backend has
   * DocuSign email the signer again on a DocuSign step, or sends our reminder
   * email otherwise.
   */
  async remindTask(id: string): Promise<{ reminded: boolean; channel: 'docusign' | 'email' }> {
    return apiClient.post(`/inbox-tasks/${id}/remind`, {});
  }

  // Reminder visibility for a task: last sent (auto or manual), next automatic
  // due date, count sent, and whether the tenant paused notifications.
  async getReminderStatus(id: string): Promise<ReminderStatus> {
    return apiClient.get<ReminderStatus>(`/inbox-tasks/${id}/reminder-status`);
  }

  async signMandate(mandateId: string, input: {
    signature_method: 'manual' | 'electronic'
    signature_type?: 'draw' | 'type'
    signature_data?: string
    signer_name: string
    consent_text: string
    document_hash?: string
  }): Promise<{ id: string; signed_at: string }> {
    return apiClient.post(`/signatures/mandates/${mandateId}/sign`, input);
  }

}

export const inboxTasksService = new InboxTasksService();